import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, ParamMap } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Select, Store } from '@ngxs/store';
import { CheckoutPaymentInformationState, CreditCardValidationError, FlowState, PaymentMethodDataService } from '@platform-frontend/checkout';
import {
  CreatePaymentMethod,
  EditableCreditCard,
  EditableDirectDebit,
  EditablePaymentMethod,
  PaymentMethodType,
  UpdatePaymentMethod
} from '@platform-frontend/marketplace';
import { Navigate, SimpleRouterState, handleSpecificErrors } from '@platform-frontend/util';
import { Observable, combineLatest } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';

@UntilDestroy()
@Component({
  selector: 'yat-payment-method-with-data',
  templateUrl: './payment-method-with-data.component.html',
  styleUrls: ['./payment-method-with-data.component.scss']
})
export class PaymentMethodWithDataComponent implements OnInit {
  constructor(private paymentMethodDataService: PaymentMethodDataService, private route: ActivatedRoute, private store: Store) {}

  @Select(SimpleRouterState.queryParamMap)
  queryParams$: Observable<ParamMap>;

  editablePaymentMethod$: Observable<Partial<EditablePaymentMethod>>;
  private addOrEdit$: Observable<'add' | 'edit'>;
  paymentMethodType$: Observable<string | null>;
  validationError: CreditCardValidationError | null = null;

  ngOnInit(): void {
    const editedId$ = this.queryParams$.pipe(
      map(params => params.get('id')),
      map(stringId => (stringId === null ? null : parseInt(stringId, 10)))
    );

    const createType$ = this.queryParams$.pipe(map(params => params.get('paymentMethod') as PaymentMethodType));

    this.editablePaymentMethod$ = combineLatest([editedId$, createType$]).pipe(
      take(1),
      switchMap(([editedId, createType]) => this.paymentMethodDataService.getEditablePaymentMethod(editedId, createType))
    );

    this.addOrEdit$ = this.editablePaymentMethod$.pipe(map(editablePaymentMethod => (editablePaymentMethod.id === 0 ? 'add' : 'edit')));

    this.paymentMethodType$ = this.editablePaymentMethod$.pipe(map(paymentMethod => paymentMethod?.type ?? null));
  }

  getEditableCreditCard(editablePaymentMethod: Partial<EditablePaymentMethod> | null): Partial<EditableCreditCard> | null {
    return editablePaymentMethod?.type === 'CREDIT_CARD' ? editablePaymentMethod : null;
  }

  getEditableDirectDebit(editablePaymentMethod: Partial<EditablePaymentMethod> | null): Partial<EditableDirectDebit> | null {
    return editablePaymentMethod?.type === 'DIRECT_DEBIT' ? editablePaymentMethod : null;
  }

  save(paymentMethod: EditablePaymentMethod): void {
    this.validationError = null;
    const navExtras = { clearNotifications: false, relativeTo: this.route };
    this.addOrEdit$
      .pipe(
        take(1),
        map(addOrEdit => this.buildSaveAction(paymentMethod, addOrEdit)),
        switchMap(saveAction => this.store.dispatch(saveAction)),
        switchMap(() => this.store.dispatch(new Navigate([this.getProceedUrl()], navExtras))),
        handleSpecificErrors({
          CVC_DECLINED: () => (this.validationError = 'CVC_DECLINED'),
          CARD_EXPIRED: () => (this.validationError = 'CARD_EXPIRED'),
          EXPIRY_DATE_INVALID: () => (this.validationError = 'EXPIRY_DATE_INVALID'),
          INVALID_CARD_NUMBER: () => (this.validationError = 'INVALID_CARD_NUMBER')
        }),
        untilDestroyed(this)
      )
      .subscribe();
  }

  private buildSaveAction(paymentMethod: EditablePaymentMethod, addOrEdit: 'add' | 'edit'): CreatePaymentMethod | UpdatePaymentMethod {
    const flow = this.store.selectSnapshot(FlowState.flow);

    if (addOrEdit === 'add') {
      return new CreatePaymentMethod(paymentMethod, flow.type === 'SUBSCRIPTION_MANAGEMENT' ? true : false);
    } else {
      const overrideSubscription =
        flow.type === 'SUBSCRIPTION_MANAGEMENT'
          ? { productIdentifier: flow.startParams.productIdentifier, contractId: flow.startParams.contractId }
          : undefined;
      return new UpdatePaymentMethod(paymentMethod, overrideSubscription);
    }
  }

  private getProceedUrl(): string {
    const paymentMethodCount = this.store.selectSnapshot(CheckoutPaymentInformationState.paymentMethods).length;
    if (paymentMethodCount <= 1) {
      return '../overview';
    } else {
      return '../payment-method-selection';
    }
  }

  cancel(): void {
    const navExtras = { relativeTo: this.route };
    this.addOrEdit$
      .pipe(
        take(1),
        map(addOrEdit => this.getCancelUrl(addOrEdit)),
        switchMap(cancelUrl => this.store.dispatch(new Navigate([cancelUrl], navExtras))),
        untilDestroyed(this)
      )
      .subscribe();
  }

  private getCancelUrl(addOrEdit: 'add' | 'edit'): string {
    const paymentMethodCount = this.store.selectSnapshot(CheckoutPaymentInformationState.paymentMethods).length;
    if (paymentMethodCount === 0) {
      return '../overview';
    } else if (addOrEdit === 'add') {
      return '../add-payment-method';
    } else {
      return '../payment-method-selection';
    }
  }
}
